import React from "react";
import { ShoppingBag, Clock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { avatarSrc } from "@/lib/defaults";
import { useApp } from "@/context/AppContext";
import { PromoBadge, promoEndsLabel } from "@/components/marketplace/Promo";

// Lot 697 — carte d'une annonce en promo partagée dans une story (« Partager la promo en story ») : photo + badge
// -X %, titre, prix promo / prix barré, « Jusqu'au… », vendeur. `compact` = vignette de la rangée des stories.
// Jamais cliquable par elle-même : dans la story, c'est le bouton du cadre (StoryCardFrame) qui ouvre l'annonce.
export const SharedListingCard = ({ sl, compact = false, testId = "shared-listing-card" }) => {
  const { t, lang } = useApp();
  if (!sl) return null;
  const img = sl.image ? resolveMediaUrl(sl.image) : null;
  const seller = sl.seller || {};
  if (compact) {
    return (
      <div className="relative w-full rounded-md overflow-hidden text-left" style={{ background: "rgba(255,255,255,0.95)", color: "#1f2937" }} data-testid={testId}>
        {img ? <img src={img} alt="" loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />
          : <div className="w-full aspect-[4/3] grid place-items-center" style={{ background: "#f3f4f6" }}><ShoppingBag size={12} className="opacity-40" /></div>}
        {sl.promo && <span className="absolute top-0.5 left-0.5 rounded px-1 text-[6px] font-extrabold text-white" style={{ background: "#D64545" }}>-{sl.promo.percent} %</span>}
        <div className="px-1 py-0.5">
          <div className="text-[6px] font-bold truncate" style={{ color: "#D64545" }}>{sl.promo?.price} {sl.currency} <s style={{ color: "#6b7280" }}>{sl.price}</s></div>
          <div className="text-[6px] leading-tight line-clamp-1">{sl.title}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="block w-full text-left rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.97)", color: "#1f2937", boxShadow: "0 14px 44px rgba(0,0,0,0.35)" }} data-testid={testId}>
      <div className="relative">
        {img ? <img src={img} alt="" loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" data-testid={`${testId}-image`} />
          : <div className="w-full aspect-[4/3] grid place-items-center" style={{ background: "#f3f4f6" }}><ShoppingBag size={34} className="opacity-30" /></div>}
        {sl.promo && <PromoBadge promo={sl.promo} t={t} className="absolute bottom-2 left-2 shadow" />}
      </div>
      <div className="px-3 pt-2.5 pb-3">
        <div className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: "#D64545" }}>{t("story.shared_listing", "Annonce en promo")} · Marketplace</div>
        <div className="font-semibold text-sm leading-snug line-clamp-2 mt-0.5" data-testid={`${testId}-title`}>{sl.title}</div>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-lg font-bold" style={{ color: "#D64545" }} data-testid={`${testId}-promo-price`}>{sl.promo?.price} {sl.currency}</span>
          <s className="text-sm" style={{ color: "#6b7280" }} data-testid={`${testId}-old-price`}>{sl.price} {sl.currency}</s>
        </div>
        {sl.promo?.ends_at && <div className="text-[11px] inline-flex items-center gap-1" style={{ color: "#B42318" }}><Clock size={11} /> {promoEndsLabel(sl.promo, t, lang)}</div>}
        <div className="flex items-center gap-2 mt-2 pt-2" style={{ borderTop: "1px solid #eee" }}>
          <Avatar className="h-6 w-6"><AvatarImage src={avatarSrc(seller, 32)} /><AvatarFallback className="text-[9px]">{seller.name?.[0]}</AvatarFallback></Avatar>
          <div className="flex items-center gap-1 text-xs font-semibold truncate"><span className="truncate">{seller.name}</span>{seller.is_verified && <VerifiedBadge user={seller} size={12} interactive={false} />}</div>
        </div>
      </div>
    </div>
  );
};

export default SharedListingCard;
